/**
 * [OC-PAGES 2026-09-21, user] Draws an Order Confirmation PDF's pages as images, for Campaign History's
 * Order Confirmation tab. Replaces the browser's own PDF viewer, whose grey frame and wide scrollbar
 * could not be restyled from the page (a white frame in dark mode, a black one in light mode).
 *
 * pdf.js (pdfjs-dist 6.3.289) is loaded on first use only, so it never weighs on the app's first load.
 * It runs on the main thread (its "fake worker"): the worker module is imported and handed over on
 * `globalThis.pdfjsWorker`, which works with this app's bundler without serving a separate worker
 * file. An OC is a few pages, so the main thread is plenty.
 * [DEPS 2026-09-24] pdf.js 6 has no code path that compiles code out of a document any more, so the old
 * `isEvalSupported: false` option is gone from its API. It needs Chrome 125+ / Safari 18+; on an older
 * browser drawing fails and the card falls back to "Open full size".
 */
type PdfJs = typeof import('pdfjs-dist');

let loading: Promise<PdfJs> | null = null;

function loadPdfJs(): Promise<PdfJs> {
  if (!loading) {
    loading = Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs')])
      .then(([lib, worker]) => {
        (globalThis as any).pdfjsWorker = worker;
        return lib;
      });
    // A failed load (say, a network blip) must not stick for the rest of the session.
    loading.catch(() => { loading = null; });
  }
  return loading;
}

/** Start loading pdf.js early, so it is ready by the time the bytes arrive. */
export function preloadPdfJs(): void {
  loadPdfJs().catch(() => {});
}

/**
 * Draws every page of `bytes` at `widthPx` device pixels wide and hands each one over as an image
 * URL (object URL, PNG) as soon as it is drawn, so page 1 shows before the rest. The caller owns the
 * URLs and must revoke them. `isLive` lets the caller stop the work when it no longer needs it.
 */
export async function drawPdfPages(
  bytes: ArrayBuffer,
  widthPx: number,
  onPage: (url: string) => void,
  isLive: () => boolean,
): Promise<void> {
  const pdfjs = await loadPdfJs();
  // A copy: pdf.js takes ownership of (and empties) the buffer it is given, and the caller's buffer
  // may be the one the GET cache holds.
  const task = pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)) });
  const doc = await task.promise;
  try {
    for (let n = 1; n <= doc.numPages && isLive(); n += 1) {
      const page = await doc.getPage(n);
      const viewport = page.getViewport({ scale: widthPx / page.getViewport({ scale: 1 }).width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, viewport }).promise;
      page.cleanup();
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('page could not be encoded');
      if (!isLive()) return;
      onPage(URL.createObjectURL(blob));
    }
  } finally {
    task.destroy(); // pdf.js 6 moved destroy() from the document to its loading task
  }
}
